<template>
    <div class="apply-credit py-6">
      <p class="apply-credit-title mb-2">Applying Credits</p>
      <p class="available-credit mb-2">
        You have
        <span class="available-credit-amount">
          {{ currency.symbol }}{{ displayAvailableCredit }}
        </span>
        in available credit
      </p>
      <div v-if="!creditApplied" class="flex flex-row" style="width: 494px;">
        <el-input
          v-model="credit"
          :class="['credit-input-bar', {'error-input': exceedsLimit}]"
          :placeholder="`Available: ${currency.symbol + displayAvailableCredit}`"
          @input="validateNumberInput">  
          <template v-if="credit && credit.length > 0"#prefix>
            <div class="dollar-sign">{{ currency.symbol }}</div>
          </template>
          <template #suffix>
            <el-button
            v-if="credit && credit.length > 0"
            class="clear-credit-btn"
            @click="clearCredit"
            >Clear</el-button>
          </template>
        </el-input>
        <button class="apply-credit-btn" :disabled="!credit" :class="{'disabled': !credit}" @click="applyCredit">Apply</button>

      </div>
      <Alert
        v-if="exceedsLimit"
        type="error"
        class="mt-2"
        style="width: 494px;"
      >
        Your typed amount exceeds the credits available
      </Alert>
      <Alert
        v-if="creditApplied"
        type="info"
        class="mt-2"
        style="width: 494px;"
      >
        <div class="flex justify-between w-full">
          <span>
            Your credit of
            <span class="available-credit-amount">
              {{ currency.symbol }}{{ credit }}
            </span>
            has been applied successfully!
          </span>
          <span class="cursor-pointer text-blue-500" @click="resetCredit">Remove</span>
        </div>
      </Alert>
    </div>
</template>

<script>
import Alert from "@/NewLibrary/Alert.vue";

export default {
  name: "ApplyCredit",
  components: {
    Alert
  },
  props: {
    availableCredit: {
      type: Number,
      default: 0
    },
    currency: {
      type: Object,
      default: () => ({ symbol: '$' })
    }
  },
  data() {
    return {
      credit: "",
      exceedsLimit: false,
      creditApplied: false,
      appliedAmount: 0,
      remainingCredit: 0
    }
  },
  computed: {
    displayAvailableCredit() {
      const credit = this.creditApplied ? this.remainingCredit : (this.availableCredit || 0);
      return credit.toFixed(2);
    }
  },
  methods: {
    applyCredit() {
      this.checkExceedsLimit();
      if (this.credit && !this.exceedsLimit) {
        const amount = parseFloat(this.credit);
        if (!isNaN(amount)) {
          this.appliedAmount = amount;
          this.remainingCredit = (this.availableCredit || 0) - amount;
          this.creditApplied = true;
          this.$emit('apply-credit', amount);
        }
      }
    },
    clearCredit() {
      this.credit = "";
      this.exceedsLimit = false;
    },
    resetCredit() {
      this.creditApplied = false;
      this.credit = "";
      this.exceedsLimit = false;
      this.appliedAmount = 0;
      this.$emit('reset-credit');
    },
    validateNumberInput() {
      // only allow numbers and decimal
      this.credit = this.credit.replace(/[^0-9.]/g, '');
      // max 2 decimal places
      const parts = this.credit.split('.');
      if (parts.length > 2) {
        this.credit = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // if decimal part exceeds 2 digits, truncate it
      if (parts.length > 1 && parts[1].length > 2) {
        this.credit = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      // Reset the error state when input changes, it will be re-checked on apply
      if (this.exceedsLimit) {
        this.exceedsLimit = false;
      }
    },
    
    checkExceedsLimit() {
      // if input is empty, do not show warning
      if (!this.credit) {
        this.exceedsLimit = false;
        return;
      }
      
      // convert input to number and compare with available credit
      const inputAmount = parseFloat(this.credit);
      const availableCredit = this.availableCredit || 0;
      this.exceedsLimit = !isNaN(inputAmount) && inputAmount > availableCredit;
    }
  }
}
</script>
<style scoped>
.apply-credit {
  width: 100%;
}

.apply-credit-title {
  color: #000;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.458px;
}

.available-credit {
  color: var(--Gray-100, #161616);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.458px;
  color: var(--States-Focus-ring-default, #1C2024);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.458px;
}

.available-credit-amount {
  color: var(--color-Blue-000, #3498DB);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.458px;
}

.apply-credit-btn {
  display: flex;
  width: 140px;
  align-items: center;
  gap: 8px;
  display: flex;
  height: 36px;
  padding: var(--V-padding, 12px) var(--H-padding, 20px);
  justify-content: center;
  align-items: center;
  gap: var(--Spacing, 8px);
  border-radius: var(--radius, 4px);
  border: 1px solid var(--color-Cyan-000, #20A7C1);
  margin-left: 8px;
  color: var(--color-Cyan-000, #20A7C1);
  text-align: center;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.dollar-sign {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #606266;
  font-weight: 500;
  margin-right: 2px !important;
}

.clear-credit-btn {
  color: var(--color-Red-000, #FF1D00);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  border: none !important;
}

.clear-credit-btn:hover {
  color: var(--color-Red-000, #FF1D00) !important;
  border: none !important;
  background: transparent !important;
}

.apply-credit-btn.disabled,
.apply-credit-btn:disabled {
  display: flex;
  height: 36px;
  padding: var(--V-padding, 12px) var(--H-padding, 20px);
  justify-content: center;
  align-items: center;
  gap: var(--Spacing, 8px);
  border-radius: var(--radius, 4px);
  background: var(--color-Grey-Grey-E6, #E6E6E6);
  border: none;
  color: #999;
  cursor: not-allowed;
}
</style>
